import { Injectable, OnDestroy, Signal } from '@angular/core';
import {
  notificationsSettingsMutation,
  notificationsSettingsQuery,
} from '@graphql/notifications';
import { CognitoService, UserWithID } from '@services/cognito/cognito.service';
import { getErrorMessage } from '@utils/errors';
import { Apollo } from 'apollo-angular';
import {
  Event_Category,
  Notification_Type,
  NotificationsSettings,
  NotificationsSettingsQuery,
  UpdateNotificationsSettingsMutation,
} from 'gql/graphql';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { ToastService } from './toast.service';
import { TranslateService } from '@ngx-translate/core';

interface InAppPush {
  [Event_Category.Critical]: boolean;
  [Event_Category.Information]: boolean;
  [Event_Category.System]: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationsService implements OnDestroy {
  inAppPushEnabled$ = new BehaviorSubject<InAppPush>({
    [Event_Category.Critical]: false,
    [Event_Category.Information]: false,
    [Event_Category.System]: false,
  });
  user: Signal<UserWithID | null>;
  notificationsSettings$ = new BehaviorSubject<NotificationsSettings | null>(
    null
  );

  private subscriptions: Subscription[] = [];
  constructor(
    private readonly cognitoService: CognitoService,
    private readonly apollo: Apollo,
    private readonly toast: ToastService,
    private readonly translate: TranslateService
  ) {
    this.user = cognitoService.loggedUser;
    // TODO: far partire solo se l'utente è loggato!
    if (this.user()?.id) {
      this.subscriptions.push(
        apollo
          .watchQuery<NotificationsSettingsQuery>({
            query: notificationsSettingsQuery,
          })
          .valueChanges.subscribe({
            next: ({ data }) => {
              this.notificationsSettings$.next({
                ...data.notificationsSettings,
              });
              this.inAppPushEnabled$.next({
                [Event_Category.Critical]:
                  data.notificationsSettings.CRITICAL.INAPP_PUSH,
                [Event_Category.Information]:
                  data.notificationsSettings.INFORMATION.INAPP_PUSH,
                [Event_Category.System]:
                  data.notificationsSettings.SYSTEM.INAPP_PUSH,
              });
            },
            error: (err) => {
              //let errMsg = getErrorMessage(err, this.translate);
              //this.toast.notifyError(errMsg);
            },
          })
      );
    }
  }

  getNotificationsSettings(): Observable<NotificationsSettings | null> {
    return this.notificationsSettings$.asObservable();
  }

  updateNotificationsSettings(
    eventType: Event_Category,
    notificationType: Notification_Type,
    notificationOn: boolean
  ) {
    this.subscriptions.push(
      this.apollo
        .mutate<UpdateNotificationsSettingsMutation>({
          mutation: notificationsSettingsMutation,
          variables: {
            eventType: eventType,
            notification: {
              notificationType: notificationType,
              notificationOn,
            },
          },
        })
        .subscribe({
          next: ({ data }) => {
            if (!data || !data.updateNotificationsSettings) {
              return;
            }
            this.notificationsSettings$.next({
              ...data.updateNotificationsSettings,
            });
            this.inAppPushEnabled$.next({
              [Event_Category.Critical]:
                data.updateNotificationsSettings.CRITICAL.INAPP_PUSH,
              [Event_Category.Information]:
                data.updateNotificationsSettings.INFORMATION.INAPP_PUSH,
              [Event_Category.System]:
                data.updateNotificationsSettings.SYSTEM.INAPP_PUSH,
            });
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        })
    );
  }

  toggleInAppPush(eventType: Event_Category, disabled: boolean): void {
    this.inAppPushEnabled$.next({
      ...this.inAppPushEnabled$.value,
      [eventType]: disabled,
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
